/**
 * App-level provider composition — Solid equivalent of
 * `AppProviders` in `apps/native/shared/contexts/app-providers.tsx`.
 */

import { QueryClientProvider } from "@tanstack/solid-query";
import type { ParentComponent } from "solid-js";

import { queryClient } from "@/lib/query-client";
import { PowerSyncProvider } from "@/providers/power-sync-context";
import { ThemeProvider } from "@/providers/theme-provider";
import { TypographyProvider } from "@/providers/typography-provider";
import { I18nProvider } from "@/providers/i18n-provider";
import { defaultCommands, defaultKeymap } from "@/lib/shortcuts/defaults";
import { ShortcutsProvider } from "@fleet/shortcuts";
import { InteractionProvider, useInteraction } from "@fleet/ui";

import { SessionBridge } from "@/components/session-bridge";
import { UpdateStalledBanner } from "@/components/update-stalled-banner";
import { UserPreferencesProvider } from "@/providers/user-preferences-context";
import { KernelProvider } from "@/providers/kernel-provider";

const ShortcutsWithInteraction: ParentComponent = (props) => {
  const interaction = useInteraction();
  return (
    <ShortcutsProvider
      initialCommands={defaultCommands}
      initialBindings={defaultKeymap}
      context={interaction.state}
    >
      {props.children}
    </ShortcutsProvider>
  );
};

export const AppProviders: ParentComponent = (props) => (
  <QueryClientProvider client={queryClient}>
    <SessionBridge />
    <PowerSyncProvider>
      <UserPreferencesProvider>
        <ThemeProvider>
          <TypographyProvider>
            <I18nProvider>
              {/* Only ever visible if the silent self-update gave up. */}
              <UpdateStalledBanner />
              <InteractionProvider>
                <ShortcutsWithInteraction>
                  <KernelProvider>{props.children}</KernelProvider>
                </ShortcutsWithInteraction>
              </InteractionProvider>
            </I18nProvider>
          </TypographyProvider>
        </ThemeProvider>
      </UserPreferencesProvider>
    </PowerSyncProvider>
  </QueryClientProvider>
);
